import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Award, Download } from "lucide-react"
import { IDD_SOLL_MINUTEN, type IddNachweis } from "@nl/shared"

import { formatDatum, formatMinuten } from "@/components/idd/idd-format"
import { IDD_ART_TITEL } from "@/components/idd/idd-module"
import { LoadState } from "@/components/load-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { useIddNachweise } from "@/lib/idd-queries"

function nachJahren(nachweise: IddNachweis[]): [number, IddNachweis[]][] {
  const jahre = new Map<number, IddNachweis[]>()
  for (const n of nachweise) jahre.set(n.jahr, [...(jahre.get(n.jahr) ?? []), n])
  return [...jahre.entries()].sort(([a], [b]) => b - a)
}

function Jahr({ jahr, nachweise }: { jahr: number; nachweise: IddNachweis[] }) {
  const minuten = nachweise.reduce((s, n) => s + n.minuten, 0)
  return (
    <section aria-labelledby={`jahr-${jahr}`} className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`jahr-${jahr}`} className="font-display text-[22px] font-medium tracking-tight">
          {jahr}
        </h2>
        <p className="text-[13px] tabular-nums text-muted-foreground">
          {formatMinuten(minuten)} von {formatMinuten(IDD_SOLL_MINUTEN)} IDD-Zeit
        </p>
      </div>
      <ul className="divide-y divide-[var(--rule)] rounded-md bg-card ring-1 ring-foreground/10">
        {nachweise.map((n) => (
          <li key={n.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-5">
            <Award className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0 flex-1">
              <Link
                to="/idd/nachweis/$id"
                params={{ id: n.id }}
                className="block text-[14px] font-medium hover:underline"
              >
                {n.titel}
              </Link>
              <p className="text-[12.5px] text-muted-foreground">
                {IDD_ART_TITEL[n.art]} · {formatDatum(n.abgeschlossenAm)} ·{" "}
                <span className="tabular-nums">{formatMinuten(n.minuten)}</span>
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/idd/nachweis/$id" params={{ id: n.id }} search={{ drucken: true }}>
                <Download className="h-4 w-4" aria-hidden />
                Herunterladen
                <span className="sr-only">: {n.titel}</span>
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ZertifikatePage() {
  const { data, isError, refetch } = useIddNachweise()
  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/idd">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          IDD-Bereich
        </Link>
      </Button>
      <PageHeader
        title="IDD-Zertifikate"
        description="Alle Nachweise deiner IDD-Weiterbildung, nach Jahren sortiert."
        actions={
          data && data.length > 0 ? (
            <Button asChild>
              <Link to="/idd/zertifikate/alle">
                <Download className="h-4 w-4" aria-hidden />
                Alle Zertifikate herunterladen
              </Link>
            </Button>
          ) : undefined
        }
      />
      {!data ? (
        <LoadState failed={isError} onRetry={() => void refetch()}>
          Deine Zertifikate konnten nicht geladen werden.
        </LoadState>
      ) : data.length === 0 ? (
        <p className="rounded-md bg-card px-5 py-6 text-[14px] text-muted-foreground ring-1 ring-foreground/10">
          Noch keine Zertifikate. Sie entstehen automatisch, sobald du ein Training im
          IDD-Bereich abgeschlossen hast.
        </p>
      ) : (
        nachJahren(data).map(([jahr, nachweise]) => (
          <Jahr key={jahr} jahr={jahr} nachweise={nachweise} />
        ))
      )}
    </div>
  )
}

export const Route = createFileRoute("/_app/idd/zertifikate/")({
  component: ZertifikatePage,
})
